import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { CircularProgress } from '@material-ui/core'
import { toast } from 'react-toastify'

const fmt$ = (v) => v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'
const fmt$0 = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const fmtQty = (v) => Number(v || 0).toLocaleString('en-US')

const COMPONENT_TYPE_COLORS = {
  labor: { bg: '#e3f2fd', color: '#1565c0', label: 'L' },
  equipment: { bg: '#fff3e0', color: '#ef6c00', label: 'E' },
  material: { bg: '#e8f5e9', color: '#2e7d32', label: 'M' },
  subcontract: { bg: '#fce4ec', color: '#c62828', label: 'S' },
  disposal: { bg: '#f3e5f5', color: '#6a1b9a', label: 'D' },
  allowance: { bg: '#eceff1', color: '#546e7a', label: 'A' }
}

function ServiceSidebar ({ services, selectedId, onSelect, materialsByService, bidItemsByService, onAddService }) {
  return (
    <aside style={{
      position: 'sticky',
      top: 0,
      alignSelf: 'start',
      height: 'calc(100vh - 170px)',
      overflow: 'auto',
      borderLeft: '1px solid var(--df-line, #ddd)',
      background: 'var(--df-surface, #fff)',
      padding: 18
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--df-ink, #222)', lineHeight: 1.15 }}>Project services</div>
          <div style={{ fontSize: 12, color: 'var(--df-ink-3, #888)', marginTop: 2 }}>{services.length} on project</div>
        </div>
        <button className="hmi-btn hmi-btn-sm hmi-btn-ghost" onClick={onAddService} style={{ fontSize: 12, padding: '4px 10px' }}>
          + Add
        </button>
      </div>
      <div style={{ display: 'grid', gap: 7 }}>
        {services.map(svc => {
          const isSelected = svc._id === selectedId
          const matCount = (materialsByService[svc._id] || []).length
          const bidRowCount = (bidItemsByService[svc.serviceId] || []).length
          const exportRowCount = (svc.outputAllocations || []).filter(a => a.bidItemId).length
          const recipeCount = (svc.components || []).length
          const complete = exportRowCount > 0 && recipeCount > 0
          return (
            <button
              key={svc._id}
              onClick={() => onSelect(svc._id)}
              style={{
                width: '100%',
                textAlign: 'left',
                border: `1px solid ${isSelected ? 'var(--df-primary, #2e7d32)' : 'transparent'}`,
                background: isSelected ? 'var(--df-primary-soft, #e8f5e9)' : 'transparent',
                borderRadius: 8,
                padding: '9px 10px',
                cursor: 'pointer',
                color: 'var(--df-ink, #222)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <strong style={{ lineHeight: 1.25 }}>{svc.serviceName || svc.serviceId}</strong>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: complete ? '#4a7c59' : '#d6a33d', marginTop: 5, flexShrink: 0 }} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--df-ink-3, #888)', textTransform: 'uppercase', letterSpacing: 1.2, marginTop: 4 }}>
                {svc.serviceCategory}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
                <span style={railCountStyle}>{bidRowCount} rows</span>
                <span style={railCountStyle}>{matCount} mat</span>
                <span style={railCountStyle}>{exportRowCount} export</span>
              </div>
            </button>
          )
        })}
      </div>
      {services.length === 0 && (
        <div style={{ padding: '20px 12px', fontSize: 12, color: 'var(--ink-3, #888)', textAlign: 'center' }}>
          No services yet. Match bid rows first or add a service manually.
        </div>
      )}
    </aside>
  )
}

const railCountStyle = {
  borderRadius: 999,
  background: 'var(--df-bg-2, #f0ede6)',
  color: 'var(--df-ink-3, #777)',
  padding: '1px 7px',
  fontSize: 10,
  fontWeight: 800
}

function BidItemsSection ({ bidItems }) {
  if (!bidItems || bidItems.length === 0) return null
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3, #888)', marginBottom: 6 }}>
        Matched Bid Rows
      </div>
      <div style={{ border: '1px solid var(--line-2, #ddd)', borderRadius: 8, overflow: 'hidden' }}>
        {bidItems.map((item, i) => (
          <div key={item._id || i} style={{
            display: 'flex', alignItems: 'center', padding: '6px 12px', gap: 12,
            borderBottom: i < bidItems.length - 1 ? '1px solid var(--line-2, #eee)' : 'none',
            fontSize: 13, background: i % 2 === 0 ? 'transparent' : 'var(--bg-panel-alt, #faf8f3)'
          }}>
            <span style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)', minWidth: 30 }}>#{item.itemNo || '—'}</span>
            <span style={{ flex: 1 }}>{item.description || item.descriptionNormalized || '—'}</span>
            <span style={{ fontSize: 12, color: 'var(--ink-3)', minWidth: 60, textAlign: 'right' }}>{fmtQty(item.quantity)} {item.unit || ''}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function MaterialsSection ({ serviceMaterials, allMaterials, serviceId, projectId, url, onRefresh }) {
  const [showPicker, setShowPicker] = useState(false)
  const [pickerSearch, setPickerSearch] = useState('')
  const [allocQty, setAllocQty] = useState({})
  const [editingCost, setEditingCost] = useState(null)
  const [costInput, setCostInput] = useState('')

  const handleCostSave = async (materialId) => {
    const val = parseFloat(costInput)
    if (isNaN(val) || val < 0) { setEditingCost(null); return }
    try {
      await axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${materialId}/override-price`, { manualOverride: val })
      setEditingCost(null)
      onRefresh()
    } catch (e) {
      toast.error('Price update failed: ' + (e.response?.data?.error || e.message))
    }
  }

  const handleAllocate = async (materialId, qty) => {
    try {
      await axios.post(`${url}/api/bid-estimation/projects/${projectId}/materials/${materialId}/assign-service`, {
        projectServiceId: serviceId,
        allocatedQty: qty
      })
      toast.success('Material allocated')
      onRefresh()
      setShowPicker(false)
    } catch (e) {
      const msg = e.response?.data?.error || e.message
      if (msg.includes('exceeds')) toast.error(msg)
      else toast.error('Allocation failed: ' + msg)
    }
  }

  const handleRemove = async (materialId, allocationId) => {
    try {
      await axios.delete(`${url}/api/bid-estimation/projects/${projectId}/materials/${materialId}/service-allocations/${allocationId}`)
      toast.info('Material removed from service')
      onRefresh()
    } catch (e) {
      toast.error('Remove failed: ' + e.message)
    }
  }

  const handleUpdateQty = async (materialId, allocationId, newQty) => {
    try {
      await axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${materialId}/service-allocations/${allocationId}`, {
        allocatedQty: newQty
      })
      onRefresh()
    } catch (e) {
      toast.error(e.response?.data?.error || 'Update failed')
    }
  }

  const availableMaterials = useMemo(() => {
    if (!allMaterials) return []
    return allMaterials.filter(m => {
      if (m.status === 'skipped') return false
      const totalAllocated = (m.serviceAllocations || []).reduce((s, a) => s + (a.allocatedQty || 0), 0)
      return totalAllocated < (m.totalQty || 0)
    }).filter(m => {
      if (!pickerSearch.trim()) return true
      const q = pickerSearch.toLowerCase()
      return (m.canonicalName || '').toLowerCase().includes(q) || (m.category || '').toLowerCase().includes(q)
    })
  }, [allMaterials, pickerSearch])

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3, #888)' }}>
          Project Materials
        </div>
        <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={() => setShowPicker(!showPicker)}>
          {showPicker ? 'Cancel' : '+ Add Material'}
        </button>
      </div>

      {serviceMaterials.length > 0 && (
        <div style={{ border: '1px solid var(--line-2, #ddd)', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
          <div style={{ display: 'flex', padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', borderBottom: '1px solid var(--line-2, #ddd)', background: 'var(--bg-panel-alt, #faf8f3)' }}>
            <span style={{ flex: 1 }}>Material</span>
            <span style={{ width: 80, textAlign: 'right' }}>Qty</span>
            <span style={{ width: 80, textAlign: 'right' }}>Unit Cost</span>
            <span style={{ width: 90, textAlign: 'right' }}>Ext Cost</span>
            <span style={{ width: 40 }} />
          </div>
          {serviceMaterials.map(({ projectMaterial: pm, allocatedQty, allocationId, allocatedCost }) => (
            <div key={pm._id} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--line-2, #eee)', fontSize: 13 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{pm.canonicalName}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{pm.category} · {fmtQty(pm.totalQty)} {pm.unit} total</div>
              </div>
              <div style={{ width: 80, textAlign: 'right' }}>
                <input
                  type="number"
                  value={allocatedQty}
                  onChange={e => handleUpdateQty(pm._id, allocationId, Number(e.target.value))}
                  style={{
                    width: 60, textAlign: 'right', padding: '2px 4px', border: '1px solid var(--line-2)',
                    borderRadius: 4, fontSize: 13, background: 'transparent'
                  }}
                />
              </div>
              <div style={{ width: 80, textAlign: 'right' }}>
                {editingCost === pm._id ? (
                  <input
                    type="number"
                    autoFocus
                    value={costInput}
                    onChange={e => setCostInput(e.target.value)}
                    onBlur={() => handleCostSave(pm._id)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCostSave(pm._id); if (e.key === 'Escape') setEditingCost(null) }}
                    style={{ width: 70, textAlign: 'right', padding: '2px 4px', border: '1px solid var(--brand, #2e7d32)', borderRadius: 4, fontSize: 12, background: 'var(--bg-panel-alt)' }}
                  />
                ) : (
                  <span
                    onClick={() => { setEditingCost(pm._id); setCostInput(String(pm.effectiveUnitCost || pm.unitCost || '')) }}
                    style={{ cursor: 'pointer', fontSize: 12, color: pm.effectiveUnitCost && !pm.unitCost ? '#ff9800' : 'var(--ink-2)', borderBottom: '1px dashed var(--line-2, #ccc)' }}
                    title="Click to edit unit cost"
                  >
                    {fmt$(pm.effectiveUnitCost || pm.unitCost)}
                  </span>
                )}
              </div>
              <span style={{ width: 90, textAlign: 'right', fontWeight: 600 }}>{fmt$(allocatedCost)}</span>
              <button
                onClick={() => handleRemove(pm._id, allocationId)}
                style={{ width: 40, textAlign: 'center', background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 16 }}
                title="Remove from service"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {serviceMaterials.length === 0 && !showPicker && (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, border: '1px dashed var(--line-2)', borderRadius: 8 }}>
          No materials assigned. Click "Add Material" to assign project materials to this service.
        </div>
      )}

      {showPicker && (
        <div style={{ border: '1px solid var(--brand, #2e7d32)', borderRadius: 8, padding: 12, background: 'var(--bg-panel-alt, #faf8f3)' }}>
          <input
            type="text"
            placeholder="Search available materials..."
            value={pickerSearch}
            onChange={e => setPickerSearch(e.target.value)}
            autoFocus
            style={{
              width: '100%', height: 32, padding: '0 10px', borderRadius: 6,
              border: '1px solid var(--line-2)', fontSize: 13, marginBottom: 8
            }}
          />
          <div style={{ maxHeight: 250, overflow: 'auto' }}>
            {availableMaterials.map(m => {
              const totalAllocated = (m.serviceAllocations || []).reduce((s, a) => s + (a.allocatedQty || 0), 0)
              const remaining = (m.totalQty || 0) - totalAllocated
              const key = m._id
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', gap: 8, borderBottom: '1px solid var(--line-2, #eee)', fontSize: 13 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{m.canonicalName}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                      {m.category} · {fmtQty(remaining)} of {fmtQty(m.totalQty)} {m.unit} remaining
                      {(m.unitCost > 0 || m.materialCatalogItemId?.bestPrice > 0) && <> · {fmt$(m.unitCost || m.materialCatalogItemId?.bestPrice)}/{m.unit}</>}
                    </div>
                  </div>
                  <input
                    type="number"
                    placeholder={String(remaining)}
                    value={allocQty[key] ?? ''}
                    onChange={e => setAllocQty(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{
                      width: 70, textAlign: 'right', padding: '2px 4px',
                      border: '1px solid var(--line-2)', borderRadius: 4, fontSize: 13
                    }}
                  />
                  <button
                    className="hmi-btn hmi-btn-primary hmi-btn-sm"
                    onClick={() => handleAllocate(m._id, Number(allocQty[key]) || remaining)}
                  >
                    Assign
                  </button>
                </div>
              )
            })}
            {availableMaterials.length === 0 && (
              <div style={{ padding: 12, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
                No unallocated materials found.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ComponentsSection ({ components }) {
  if (!components || components.length === 0) return null

  const grouped = {}
  for (const c of components) {
    const type = c.costType || c.type || 'other'
    if (!grouped[type]) grouped[type] = []
    grouped[type].push(c)
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3, #888)', marginBottom: 6 }}>
        Recipe Components
      </div>
      <div style={{ border: '1px solid var(--line-2, #ddd)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', borderBottom: '1px solid var(--line-2, #ddd)', background: 'var(--bg-panel-alt, #faf8f3)' }}>
          <span style={{ width: 30 }}>Type</span>
          <span style={{ flex: 1, paddingLeft: 8 }}>Description</span>
          <span style={{ width: 70, textAlign: 'right' }}>Qty</span>
          <span style={{ width: 60, textAlign: 'right' }}>Unit</span>
          <span style={{ width: 80, textAlign: 'right' }}>Rate</span>
          <span style={{ width: 90, textAlign: 'right' }}>Total</span>
        </div>
        {components.map((c, i) => {
          const meta = COMPONENT_TYPE_COLORS[c.costType || c.type] || COMPONENT_TYPE_COLORS.allowance
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: i < components.length - 1 ? '1px solid var(--line-2, #eee)' : 'none', fontSize: 13 }}>
              <span style={{ width: 30 }}>
                <span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 4, textAlign: 'center', lineHeight: '20px', fontSize: 10, fontWeight: 700, background: meta.bg, color: meta.color }}>
                  {meta.label}
                </span>
              </span>
              <span style={{ flex: 1, paddingLeft: 8 }}>{c.description || c.label || '—'}</span>
              <span style={{ width: 70, textAlign: 'right', fontFamily: 'var(--f-mono)', fontSize: 12 }}>{c.computedQty != null ? fmtQty(c.computedQty) : '—'}</span>
              <span style={{ width: 60, textAlign: 'right', fontSize: 12, color: 'var(--ink-3)' }}>{c.unit || ''}</span>
              <span style={{ width: 80, textAlign: 'right', fontSize: 12 }}>{c.unitCost != null ? fmt$(c.unitCost) : '—'}</span>
              <span style={{ width: 90, textAlign: 'right', fontWeight: 600 }}>{c.extendedCost != null ? fmt$(c.extendedCost) : '—'}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CostSummary ({ service, materialCost }) {
  const componentCost = (service.components || []).reduce((s, c) => s + (c.extendedCost || 0), 0)
  const totalCost = (service.totalCost || componentCost) + materialCost
  const totalSell = service.totalSell || totalCost

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3, #888)', marginBottom: 6 }}>
        Cost Summary
      </div>
      <div style={{
        display: 'flex', gap: 16, padding: 12, borderRadius: 8,
        background: 'var(--df-bg-2, #ece5d3)', border: '1px solid var(--line-2, #ddd)'
      }}>
        <SumStat label="Materials" value={fmt$0(materialCost)} />
        <SumStat label="Labor & Equip" value={fmt$0(componentCost)} />
        <SumStat label="Total Cost" value={fmt$0(totalCost)} bold />
        <SumStat label="Total Sell" value={fmt$0(totalSell)} bold />
      </div>
    </div>
  )
}

function SumStat ({ label, value, bold }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3, #888)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: bold ? 18 : 16, fontWeight: bold ? 700 : 600, color: 'var(--ink-1, #222)' }}>{value}</div>
    </div>
  )
}

function ServiceStatusSummary ({ bidRows, materials, exportRows, recipeComponents }) {
  const cards = [
    { label: 'Bid rows', value: bidRows, ok: bidRows > 0, hint: 'matched from bid response' },
    { label: 'Materials', value: materials, ok: true, hint: 'assigned project materials' },
    { label: 'Export rows', value: exportRows, ok: exportRows > 0, hint: 'where this service lands' },
    { label: 'Recipe', value: recipeComponents, ok: recipeComponents > 0, hint: 'labor/equipment/subcontract' }
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 16 }}>
      {cards.map(card => (
        <div key={card.label} style={{
          border: `1px solid ${card.ok ? 'rgba(74, 124, 89, 0.35)' : 'rgba(214, 163, 61, 0.5)'}`,
          background: card.ok ? 'rgba(74, 124, 89, 0.08)' : 'rgba(214, 163, 61, 0.12)',
          borderRadius: 8,
          padding: '10px 12px',
          minWidth: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--df-ink-3, #777)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{card.label}</div>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: card.ok ? '#4a7c59' : '#d6a33d', flexShrink: 0 }} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--df-ink, #222)', lineHeight: 1.1, marginTop: 5 }}>{card.value}</div>
          <div style={{ fontSize: 11, color: 'var(--df-ink-3, #777)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.hint}</div>
        </div>
      ))}
    </div>
  )
}

function AddServicePanel ({ catalogServices, projectServices, onCreate, onCancel, creating }) {
  const [serviceId, setServiceId] = useState('')
  const existingIds = useMemo(() => new Set((projectServices || []).map(s => s.serviceId)), [projectServices])
  const options = useMemo(() => {
    return (catalogServices || [])
      .filter(s => !existingIds.has(s.serviceId))
      .sort((a, b) => {
        const category = String(a.category || '').localeCompare(String(b.category || ''))
        if (category !== 0) return category
        return String(a.name || '').localeCompare(String(b.name || ''))
      })
  }, [catalogServices, existingIds])
  const selected = options.find(s => s.serviceId === serviceId)

  return (
    <div style={{ padding: 12, borderBottom: '1px solid var(--line-2, #ddd)', background: 'var(--bg-panel-alt, #faf8f3)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select
          value={serviceId}
          onChange={e => setServiceId(e.target.value)}
          style={{ flex: 1, height: 34, borderRadius: 6, border: '1px solid var(--line-2)', padding: '0 8px', background: 'var(--bg-panel, #fff)' }}
        >
          <option value="">Add another service...</option>
          {options.map(service => (
            <option key={service.serviceId} value={service.serviceId}>
              {service.category} - {service.name}
            </option>
          ))}
        </select>
        <button
          className="hmi-btn hmi-btn-primary hmi-btn-sm"
          disabled={!selected || creating}
          onClick={() => onCreate(selected)}
        >
          {creating ? 'Adding...' : 'Add'}
        </button>
        <button className="hmi-btn hmi-btn-sm hmi-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
        Add work that does not have its own bid row, then choose which bid rows carry that service in the export.
      </div>
    </div>
  )
}

function OutputAllocationSection ({ service, bidItems, url, onRefresh }) {
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [pctById, setPctById] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const nextSelected = new Set()
    const nextPct = {}
    for (const allocation of service.outputAllocations || []) {
      const bidItemId = allocation.bidItemId?._id || allocation.bidItemId
      if (!bidItemId) continue
      const key = bidItemId.toString()
      nextSelected.add(key)
      nextPct[key] = allocation.allocationPercent != null ? String(allocation.allocationPercent) : ''
    }
    setSelectedIds(nextSelected)
    setPctById(nextPct)
  }, [service._id, service.outputAllocations])

  const rebalance = (ids) => {
    const list = Array.from(ids)
    if (list.length === 0) {
      setPctById({})
      return
    }
    const base = Number((100 / list.length).toFixed(2))
    const next = {}
    list.forEach((id, index) => {
      next[id] = String(index === list.length - 1 ? Number((100 - base * (list.length - 1)).toFixed(2)) : base)
    })
    setPctById(next)
  }

  const toggle = (id) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
    rebalance(next)
  }

  const selectedTotal = Array.from(selectedIds).reduce((sum, id) => sum + Number(pctById[id] || 0), 0)
  const valid = selectedIds.size === 0 || Math.abs(selectedTotal - 100) <= 0.1

  const save = async () => {
    setSaving(true)
    try {
      const outputAllocations = Array.from(selectedIds).map(id => ({
        bidItemId: id,
        allocationBasis: 'manual_percent',
        allocationPercent: Number(pctById[id] || 0)
      }))
      await axios.put(`${url}/api/bid-estimation/project-services/${service._id}`, { outputAllocations })
      await onRefresh()
      toast.success('Bid output allocation saved')
    } catch (e) {
      toast.error('Allocation save failed: ' + (e.response?.data?.error || e.message))
    }
    setSaving(false)
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3, #888)' }}>
          Export Rows
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: valid ? 'var(--ink-3)' : '#c62828' }}>
            {selectedIds.size === 0 ? 'No export rows selected' : `${Number(selectedTotal.toFixed(2))}% assigned`}
          </span>
          <button className="hmi-btn hmi-btn-sm hmi-btn-ghost" onClick={() => rebalance(selectedIds)} disabled={selectedIds.size === 0}>
            Balance
          </button>
          <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={save} disabled={!valid || saving}>
            {saving ? 'Saving...' : 'Save export rows'}
          </button>
        </div>
      </div>
      <div style={{ border: '1px solid var(--line-2, #ddd)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', borderBottom: '1px solid var(--line-2, #ddd)', background: 'var(--bg-panel-alt, #faf8f3)' }}>
          <span style={{ width: 32 }} />
          <span style={{ width: 48 }}>#</span>
          <span style={{ flex: 1 }}>Bid row</span>
          <span style={{ width: 90, textAlign: 'right' }}>Qty</span>
          <span style={{ width: 90, textAlign: 'right' }}>Share</span>
        </div>
        {bidItems.map((item, i) => {
          const id = item._id
          const checked = selectedIds.has(id)
          const description = item.description || item.descriptionNormalized || item.descriptionRaw || '—'
          const unit = item.unit || item.unitNormalized || item.unitRaw || ''
          return (
            <div key={id} style={{ display: 'flex', alignItems: 'center', padding: '7px 12px', borderBottom: i < bidItems.length - 1 ? '1px solid var(--line-2, #eee)' : 'none', fontSize: 13, opacity: checked ? 1 : 0.62 }}>
              <span style={{ width: 32 }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(id)} />
              </span>
              <span style={{ width: 48, fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}>#{item.itemNo || '—'}</span>
              <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{description}</span>
              <span style={{ width: 90, textAlign: 'right', color: 'var(--ink-3)' }}>{fmtQty(item.quantity)} {unit}</span>
              <span style={{ width: 90, textAlign: 'right' }}>
                <input
                  type="number"
                  value={pctById[id] ?? ''}
                  disabled={!checked}
                  onChange={e => setPctById(prev => ({ ...prev, [id]: e.target.value }))}
                  style={{ width: 64, textAlign: 'right', padding: '2px 4px', border: '1px solid var(--line-2)', borderRadius: 4, fontSize: 12 }}
                /> %
              </span>
            </div>
          )
        })}
        {bidItems.length === 0 && (
          <div style={{ padding: 14, color: 'var(--ink-3)', fontSize: 13 }}>No bid rows are available for this package.</div>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
        These rows receive this service in the exported bid schedule. Use this for added services such as dump fees, cleanup, or support work that belongs across several bid rows.
      </div>
    </div>
  )
}

function BidItemAllocationSection ({ service, bidItems, materialCost }) {
  if (!bidItems || bidItems.length === 0) return null
  const totalQty = bidItems.reduce((s, b) => s + (b.quantity || 0), 0)
  const totalCost = (service.totalCost || 0) + materialCost

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3, #888)', marginBottom: 6 }}>
        Bid Item Allocation
      </div>
      <div style={{ border: '1px solid var(--line-2, #ddd)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '6px 12px', fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', borderBottom: '1px solid var(--line-2, #ddd)', background: 'var(--bg-panel-alt, #faf8f3)' }}>
          <span style={{ width: 40 }}>#</span>
          <span style={{ flex: 1 }}>Description</span>
          <span style={{ width: 70, textAlign: 'right' }}>Qty</span>
          <span style={{ width: 70, textAlign: 'right' }}>Proportion</span>
          <span style={{ width: 90, textAlign: 'right' }}>Cost</span>
          <span style={{ width: 90, textAlign: 'right' }}>Unit Price</span>
        </div>
        {bidItems.map((item, i) => {
          const proportion = totalQty > 0 ? (item.quantity || 0) / totalQty : 0
          const itemCost = proportion * totalCost
          const unitPrice = (item.quantity || 0) > 0 ? itemCost / item.quantity : 0
          return (
            <div key={item._id || i} style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: i < bidItems.length - 1 ? '1px solid var(--line-2, #eee)' : 'none', fontSize: 13 }}>
              <span style={{ width: 40, fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}>#{item.itemNo || '—'}</span>
              <span style={{ flex: 1 }}>{item.description || '—'}</span>
              <span style={{ width: 70, textAlign: 'right' }}>{fmtQty(item.quantity)}</span>
              <span style={{ width: 70, textAlign: 'right', fontSize: 12, color: 'var(--ink-3)' }}>{Math.round(proportion * 100)}%</span>
              <span style={{ width: 90, textAlign: 'right', fontWeight: 600 }}>{fmt$(itemCost)}</span>
              <span style={{ width: 90, textAlign: 'right', fontSize: 12, color: 'var(--ink-2)' }}>{fmt$(unitPrice)}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const ServiceBuildTab = ({ projectId, packageId, url }) => {
  const [projectServices, setProjectServices] = useState([])
  const [packageServices, setPackageServices] = useState([])
  const [bidItems, setBidItems] = useState([])
  const [catalogServices, setCatalogServices] = useState([])
  const [allMaterials, setAllMaterials] = useState([])
  const [serviceMaterialsMap, setServiceMaterialsMap] = useState({})
  const [selectedServiceId, setSelectedServiceId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAddService, setShowAddService] = useState(false)
  const [creatingService, setCreatingService] = useState(false)

  const fetchAll = useCallback(async () => {
    try {
      const [psRes, pkgSvcRes, matRes, itemRes, serviceRes] = await Promise.all([
        axios.get(`${url}/api/bid-estimation/projects/${projectId}/project-services`),
        axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}/services`),
        axios.get(`${url}/api/bid-estimation/projects/${projectId}/materials-unallocated`),
        axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}/items`),
        axios.get(`${url}/api/bid-estimation/services`)
      ])
      setProjectServices(psRes.data || [])
      setPackageServices(pkgSvcRes.data || [])
      setAllMaterials(matRes.data || [])
      setBidItems(itemRes.data || [])
      setCatalogServices(serviceRes.data || [])
      setLoading(false)
    } catch (e) {
      console.error('ServiceBuildTab fetch error:', e)
      setLoading(false)
    }
  }, [projectId, packageId, url])

  useEffect(() => { fetchAll() }, [fetchAll])

  useEffect(() => {
    if (projectServices.length > 0 && !selectedServiceId) {
      setSelectedServiceId(projectServices[0]._id)
    }
  }, [projectServices])

  const fetchServiceMaterials = useCallback(async (serviceId) => {
    try {
      const res = await axios.get(`${url}/api/bid-estimation/projects/${projectId}/services/${serviceId}/materials`)
      setServiceMaterialsMap(prev => ({ ...prev, [serviceId]: res.data.materials || [] }))
    } catch (e) {
      console.error('Fetch service materials error:', e)
    }
  }, [projectId, url])

  useEffect(() => {
    if (selectedServiceId) fetchServiceMaterials(selectedServiceId)
  }, [selectedServiceId, fetchServiceMaterials])

  const handleRefresh = async () => {
    if (selectedServiceId) await fetchServiceMaterials(selectedServiceId)
    const matRes = await axios.get(`${url}/api/bid-estimation/projects/${projectId}/materials-unallocated`)
    setAllMaterials(matRes.data || [])
    const psRes = await axios.get(`${url}/api/bid-estimation/projects/${projectId}/project-services`)
    setProjectServices(psRes.data || [])
  }

  const handleCreateService = async (service) => {
    setCreatingService(true)
    try {
      const res = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/project-services`, {
        serviceId: service.serviceId,
        serviceName: service.name,
        serviceCategory: service.category,
        source: 'manual'
      })
      setShowAddService(false)
      await fetchAll()
      setSelectedServiceId(res.data?._id)
      toast.success('Service added')
    } catch (e) {
      toast.error('Add service failed: ' + (e.response?.data?.error || e.message))
    }
    setCreatingService(false)
  }

  const [autoAssigning, setAutoAssigning] = useState(false)

  const handleAutoAssign = async () => {
    setAutoAssigning(true)
    try {
      const res = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/materials/auto-assign-services`)
      if (res.data.allocated > 0) {
        toast.success(`Auto-assigned materials to ${res.data.allocated} services.`)
        await fetchAll()
        if (selectedServiceId) await fetchServiceMaterials(selectedServiceId)
      } else {
        toast.info('No materials could be auto-assigned. Use "Add Material" on each service manually.')
      }
    } catch (e) {
      toast.error('Auto-assign failed: ' + (e.response?.data?.error || e.message))
    }
    setAutoAssigning(false)
  }

  const selectedService = projectServices.find(s => s._id === selectedServiceId)
  const selectedPkgService = packageServices.find(s => s.serviceId === selectedService?.serviceId)
  const bidItemsByService = useMemo(() => {
    const map = {}
    for (const svc of packageServices || []) {
      map[svc.serviceId] = svc.items || []
    }
    return map
  }, [packageServices])
  const serviceMaterials = serviceMaterialsMap[selectedServiceId] || []
  const materialCost = serviceMaterials.reduce((s, m) => s + (m.allocatedCost || 0), 0)

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
  }

  if (projectServices.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 16 }}>
        <div style={{ fontSize: 48, opacity: 0.3 }}>🔧</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink-2)' }}>No services generated yet</div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', maxWidth: 400 }}>
          Go to the Services tab to assign bid items to services, then click "Generate Services" to create ProjectServices you can build here.
        </div>
        <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={() => setShowAddService(true)}>Add service manually</button>
        {showAddService && (
          <div style={{ width: 'min(720px, 90vw)', border: '1px solid var(--line-2, #ddd)', borderRadius: 8, overflow: 'hidden' }}>
            <AddServicePanel
              catalogServices={catalogServices}
              projectServices={projectServices}
              onCreate={handleCreateService}
              onCancel={() => setShowAddService(false)}
              creating={creatingService}
            />
          </div>
        )}
      </div>
    )
  }

  const unallocatedCount = allMaterials.filter(m => !m.fullyAllocated && m.remainingQty > 0).length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {unallocatedCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
          background: 'var(--df-bg-2, #ece5d3)', borderBottom: '1px solid var(--line-2, #ddd)'
        }}>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
            {unallocatedCount} materials not yet assigned to services
          </span>
          <button
            className="hmi-btn hmi-btn-primary hmi-btn-sm"
            onClick={handleAutoAssign}
            disabled={autoAssigning}
          >
            {autoAssigning ? 'Assigning...' : 'Auto-Assign Materials to Services'}
          </button>
        </div>
      )}
      {showAddService && (
        <AddServicePanel
          catalogServices={catalogServices}
          projectServices={projectServices}
          onCreate={handleCreateService}
          onCancel={() => setShowAddService(false)}
          creating={creatingService}
        />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', flex: 1, minHeight: 0, background: 'var(--df-bg, #f7f3ea)' }}>
        <main style={{ overflow: 'auto', padding: '24px 28px 42px', minWidth: 0 }}>
          {selectedService ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--df-ink, #222)', letterSpacing: 0 }}>
                    {selectedService.serviceName || selectedService.serviceId}
                  </div>
                  <span style={{
                    border: '1px solid rgba(74, 124, 89, 0.35)',
                    background: 'rgba(74, 124, 89, 0.08)',
                    color: '#2f6841',
                    borderRadius: 999,
                    padding: '3px 9px',
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8
                  }}>
                    Build service
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-3, #888)', marginTop: 4 }}>
                  {selectedService.serviceCategory}
                  {selectedService.assemblyKey && <> · {selectedService.assemblyKey}</>}
                </div>
              </div>

              <ServiceStatusSummary
                bidRows={(selectedPkgService?.items || []).length}
                materials={serviceMaterials.length}
                exportRows={(selectedService.outputAllocations || []).filter(a => a.bidItemId).length}
                recipeComponents={(selectedService.components || []).length}
              />
              <OutputAllocationSection
                service={selectedService}
                bidItems={bidItems}
                url={url}
                onRefresh={handleRefresh}
              />
              <BidItemsSection bidItems={selectedPkgService?.items || []} />
              <MaterialsSection
                serviceMaterials={serviceMaterials}
                allMaterials={allMaterials}
                serviceId={selectedServiceId}
                projectId={projectId}
                url={url}
                onRefresh={handleRefresh}
              />
              <ComponentsSection components={selectedService.components} />
              <CostSummary service={selectedService} materialCost={materialCost} />
              <BidItemAllocationSection
                service={selectedService}
                bidItems={selectedPkgService?.items || []}
                materialCost={materialCost}
              />
            </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
              Select a service from the rail to start building.
            </div>
          )}
        </main>
        <ServiceSidebar
          services={projectServices}
          selectedId={selectedServiceId}
          onSelect={setSelectedServiceId}
          materialsByService={serviceMaterialsMap}
          bidItemsByService={bidItemsByService}
          onAddService={() => setShowAddService(true)}
        />
      </div>
    </div>
  )
}

export default ServiceBuildTab
